import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  ArrowLeft, 
  CheckCircle, 
  AlertCircle, 
  User, 
  Users, 
  Truck, 
  Building,
  RefreshCw,
  Eye,
  Save
} from 'lucide-react';
import PersonForm from '../../components/forms/PersonForm';
import Button from '../../components/ui/Button';
import Card from '../../components/ui/Card';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { useAuth } from '../../contexts/AuthContext';
import { dataHelpers } from '../../lib/supabase';
import { asaasHelpers } from '../../lib/asaasHelpers';
import toast from 'react-hot-toast';

const PersonEditPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { company } = useAuth();
  const [loading, setLoading] = useState(false);
  const [personData, setPersonData] = useState(null);
  const [loadingPerson, setLoadingPerson] = useState(true);
  const [syncingAsaas, setSyncingAsaas] = useState(false);

  const personTypes = [
    { value: 'user', label: 'Usuário', icon: User, color: 'purple' },
    { value: 'customer', label: 'Cliente', icon: Users, color: 'blue' },
    { value: 'supplier', label: 'Fornecedor', icon: Truck, color: 'orange' },
    { value: 'student', label: 'Aluno', icon: User, color: 'green' },
    { value: 'parent', label: 'Responsável', icon: Users, color: 'yellow' },
    { value: 'employee', label: 'Funcionário', icon: Building, color: 'indigo' }
  ];

  const getPersonTypeInfo = (type) => {
    return personTypes.find(t => t.value === type) || personTypes[0];
  };

  const formatPhone = (phone) => {
    if (!phone) return '';
    const numbers = phone.replace(/\D/g, '');
    if (numbers.length === 11) {
      return numbers.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
    }
    return phone;
  };

  useEffect(() => {
    if (company?.id && id) {
      loadPerson();
    }
  }, [company, id]);

  const loadPerson = async () => {
    try {
      setLoadingPerson(true);
      const person = await dataHelpers.getPerson(id, company.id);
      if (!person) {
        toast.error('Pessoa não encontrada');
        navigate('/dashboard/people');
        return;
      }
      setPersonData(person);
    } catch (error) {
      console.error('Error loading person:', error);
      toast.error('Erro ao carregar dados da pessoa');
      navigate('/dashboard/people');
    } finally {
      setLoadingPerson(false);
    }
  };

  const handleSubmit = async (formData) => {
    if (!company?.id) {
      toast.error('Empresa não encontrada');
      return;
    }

    try {
      setLoading(true);
      
      console.log('Updating person with data:', formData);

      const updatedPerson = await dataHelpers.updatePerson(id, formData);
      
      console.log('Person updated successfully:', updatedPerson);
      
      toast.success('Pessoa atualizada com sucesso!');
      
      navigate('/dashboard/people', { 
        state: { 
          message: 'Pessoa atualizada com sucesso!',
          personId: id 
        }
      });
      
    } catch (error) {
      console.error('Error updating person:', error);
      
      let errorMessage = 'Erro ao atualizar pessoa';
      
      if (error.message?.includes('duplicate')) {
        errorMessage = 'Já existe uma pessoa com este email ou documento';
      } else if (error.message?.includes('email')) {
        errorMessage = 'Este email já está cadastrado no sistema';
      } else if (error.message?.includes('document')) {
        errorMessage = 'Este documento já está cadastrado no sistema';
      } else if (error.message?.includes('company_id')) {
        errorMessage = 'Erro de autorização. Verifique se você tem permissão para editar pessoas';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/dashboard/people');
  };

  const handleSyncAsaas = async () => {
    if (!personData?.id || !company?.id) {
      toast.error('Dados da pessoa ou empresa não encontrados');
      return;
    }

    try {
      setSyncingAsaas(true);
      
      const syncResult = await asaasHelpers.syncCustomer(personData.id, company.id, true);
      
      if (syncResult.success) {
        if (syncResult.already_synced) {
          toast.success('Cliente atualizado no Asaas com sucesso!');
        } else {
          toast.success('Cliente criado no Asaas com sucesso!');
        }
        
        // Atualizar dados locais
        setPersonData(prev => ({
          ...prev,
          asaas_customer_id: syncResult.customer_id,
          asaas_sync_status: 'synced'
        }));
      } else {
        toast.error(`Erro na sincronização: ${syncResult.error}`);
      }
    } catch (error) {
      console.error('Erro ao sincronizar com Asaas:', error);
      toast.error('Erro ao sincronizar com Asaas');
    } finally {
      setSyncingAsaas(false);
    }
  };

  if (loadingPerson) {
    return (
      
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner size="large" />
        </div>
      
    );
  }

  if (!personData) {
    return (
      
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Pessoa não encontrada</h3>
            <Button onClick={() => navigate('/dashboard/people')}>
              Voltar para lista
            </Button>
          </div>
        </div>
      
    );
  }

  const typeInfo = getPersonTypeInfo(personData.type);
  const TypeIcon = typeInfo.icon;

  return (
    <div className="space-y-6">
      {/* Header Moderno com Foto */}
      <Card className="overflow-hidden">
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {/* Avatar/Foto */}
              <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                {personData.photo_url ? (
                  <img 
                    src={personData.photo_url} 
                    alt={personData.name}
                    className="w-16 h-16 rounded-full object-cover"
                  />
                ) : (
                  <User className="w-8 h-8 text-white" />
                )}
              </div>
              
              {/* Nome e informações */}
              <div>
                <h1 className="text-2xl font-bold">{personData.name}</h1>
                <div className="flex items-center mt-1 space-x-3">
                  <span className="bg-white bg-opacity-20 px-3 py-1 rounded-full text-sm font-medium flex items-center">
                    <TypeIcon className="w-4 h-4 mr-1" />
                    {typeInfo.label}
                  </span>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    personData.active 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {personData.active ? '✓ Ativa' : '✗ Inativa'}
                  </span>
                </div>
                <p className="text-blue-100 text-sm mt-2">
                  {personData.email && `📧 ${personData.email}`}
                  {personData.phone && ` • 📞 ${formatPhone(personData.phone)}`}
                </p>
              </div>
            </div>
            
            <Button
              variant="outline"
              onClick={() => navigate('/dashboard/people')}
              className="text-white border-white hover:bg-white hover:text-blue-600"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar
            </Button>
          </div>
        </div>
      </Card>

      {/* Status da Sincronização Asaas (apenas para responsáveis e funcionários) */}
      {(personData.type === 'parent' || personData.type === 'employee') && (
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Building className="w-5 h-5 text-purple-600" />
              <div>
                <h3 className="font-medium text-gray-900">Integração Asaas</h3>
                <div className="flex items-center mt-1">
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                    personData.asaas_sync_status === 'synced' 
                      ? 'bg-green-100 text-green-800' 
                      : personData.asaas_sync_status === 'error'
                      ? 'bg-red-100 text-red-800'
                      : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {personData.asaas_sync_status === 'synced' ? (
                      <>
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Sincronizado
                      </>
                    ) : personData.asaas_sync_status === 'error' ? (
                      <>
                        <AlertCircle className="w-3 h-3 mr-1" />
                        Erro
                      </>
                    ) : (
                      <>
                        <RefreshCw className="w-3 h-3 mr-1" />
                        Pendente
                      </>
                    )}
                  </span>
                  {personData.asaas_customer_id && (
                    <span className="ml-2 text-xs text-gray-500 font-mono">
                      ID: {personData.asaas_customer_id}
                    </span>
                  )}
                </div>
              </div>
            </div>
            
            <Button
              onClick={handleSyncAsaas}
              disabled={syncingAsaas}
              variant="outline"
              size="sm"
              className="flex items-center"
            >
              {syncingAsaas ? (
                <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
              ) : (
                <RefreshCw className="w-3 h-3 mr-1" />
              )}
              {syncingAsaas ? 'Sincronizando...' : 'Sincronizar'}
            </Button>
          </div>
        </Card>
      )}

      {/* Informações da empresa */}
      {company && (
        <Card className="p-4">
          <div className="flex items-center">
            <CheckCircle className="w-5 h-5 text-blue-600 mr-2" />
            <span className="text-gray-700">
              Editando pessoa da empresa: <strong className="text-gray-900">{company.name}</strong>
            </span>
          </div>
        </Card>
      )}

      {/* Formulário */}
      <PersonForm
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        loading={loading}
        initialData={personData}
      />

      {/* Dicas para edição - Design melhorado */}
      <Card className="p-6 bg-gradient-to-br from-amber-50 to-yellow-50 border-amber-200">
        <div className="flex items-start space-x-3">
          <div className="bg-amber-100 rounded-full p-2">
            <AlertCircle className="w-5 h-5 text-amber-600" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-medium text-amber-900 mb-4">💡 Dicas para edição de pessoas</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium text-amber-900 mb-3 flex items-center">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  Campos sensíveis
                </h4>
                <ul className="space-y-2 text-sm text-amber-700">
                  <li className="flex items-start">
                    <span className="w-1.5 h-1.5 bg-amber-500 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                    Alterações no email podem afetar login
                  </li>
                  <li className="flex items-start">
                    <span className="w-1.5 h-1.5 bg-amber-500 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                    Mudanças de tipo alteram funcionalidades
                  </li>
                  <li className="flex items-start">
                    <span className="w-1.5 h-1.5 bg-amber-500 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                    Documentos devem ser únicos
                  </li>
                  <li className="flex items-start">
                    <span className="w-1.5 h-1.5 bg-amber-500 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                    Desativar remove acesso/funcionalidades
                  </li>
                </ul>
              </div>
              <div>
                <h4 className="font-medium text-amber-900 mb-3 flex items-center">
                  <CheckCircle className="w-4 h-4 mr-1" />
                  Boas práticas
                </h4>
                <ul className="space-y-2 text-sm text-amber-700">
                  <li className="flex items-start">
                    <span className="w-1.5 h-1.5 bg-amber-500 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                    Comunique mudanças à pessoa
                  </li>
                  <li className="flex items-start">
                    <span className="w-1.5 h-1.5 bg-amber-500 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                    Mantenha histórico de alterações
                  </li>
                  <li className="flex items-start">
                    <span className="w-1.5 h-1.5 bg-amber-500 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                    Revise dados regularmente
                  </li>
                  <li className="flex items-start">
                    <span className="w-1.5 h-1.5 bg-amber-500 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                    Use observações para contexto
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default PersonEditPage;
